/**
 * ODataQueryBuilder - Visual OData query builder
 * 
 * Helps build OData $filter queries with a visual interface
 * Can also assist with FileMaker SQL queries via script integration
 * 
 * CHANGE HISTORY:
 * - 251201_0340 danielbeer: Created OData query builder component
 */

import React, { useState } from 'react';
import './DataSourceConfig.css';

export interface QueryCondition {
  id: string;
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'startswith' | 'endswith';
  value: string;
  logicalOp?: 'and' | 'or';
}

export interface ODataQueryBuilderProps {
  fields?: string[];
  onQueryChange: (filter: string) => void;
  onGetFields?: () => Promise<string[]>;  // Callback to get fields from FileMaker
  initialFilter?: string;
}

export function ODataQueryBuilder({
  fields: initialFields = [],
  onQueryChange,
  onGetFields,
  initialFilter = ''
}: ODataQueryBuilderProps) {
  const [conditions, setConditions] = useState<QueryCondition[]>([]);
  const [fields, setFields] = useState<string[]>(initialFields);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [showBuilder, setShowBuilder] = useState(false);

  const operatorLabels: Record<string, string> = {
    eq: 'equals',
    ne: 'not equals',
    gt: 'greater than',
    ge: 'greater than or equal',
    lt: 'less than',
    le: 'less than or equal',
    contains: 'contains',
    startswith: 'starts with',
    endswith: 'ends with'
  };

  const handleLoadFields = async () => {
    if (!onGetFields) return;
    
    setIsLoadingFields(true);
    try {
      const loadedFields = await onGetFields();
      setFields(loadedFields);
    } catch (error) {
      console.error('Failed to load fields:', error);
    } finally {
      setIsLoadingFields(false);
    }
  };

  const addCondition = () => {
    const newCondition: QueryCondition = {
      id: Date.now().toString(),
      field: fields[0] || '',
      operator: 'eq',
      value: '',
      logicalOp: conditions.length > 0 ? 'and' : undefined
    };
    setConditions([...conditions, newCondition]);
  };

  const removeCondition = (id: string) => {
    const newConditions = conditions.filter(c => c.id !== id);
    // Remove logicalOp from first condition if it exists
    if (newConditions.length > 0) {
      newConditions[0].logicalOp = undefined;
    }
    setConditions(newConditions);
    buildQuery(newConditions);
  };

  const updateCondition = (id: string, updates: Partial<QueryCondition>) => {
    const newConditions = conditions.map(c => 
      c.id === id ? { ...c, ...updates } : c
    );
    setConditions(newConditions);
    buildQuery(newConditions);
  };

  const buildQuery = (conds: QueryCondition[]) => {
    if (conds.length === 0) {
      onQueryChange('');
      return;
    }

    const parts = conds.map((cond, index) => {
      const value = cond.value.includes(' ') || isNaN(Number(cond.value))
        ? `'${cond.value}'`
        : cond.value;
      
      const condition = `${cond.field} ${cond.operator} ${value}`;
      
      if (index === 0) {
        return condition;
      }
      
      return `${cond.logicalOp} ${condition}`;
    });

    onQueryChange(parts.join(' '));
  };

  return (
    <div className="odata-query-builder">
      <div className="builder-header">
        <button 
          onClick={() => setShowBuilder(!showBuilder)}
          className="btn btn-ghost btn-sm"
        >
          {showBuilder ? '▼' : '▶'} Query Builder
        </button>
        
        {onGetFields && (
          <button 
            onClick={handleLoadFields}
            disabled={isLoadingFields}
            className="btn btn-secondary btn-sm"
            title="Load fields from FileMaker via script"
          >
            {isLoadingFields ? '⟳ Loading...' : '📋 Load Fields from FM'}
          </button>
        )}
      </div>

      {showBuilder && (
        <div className="builder-content">
          {fields.length === 0 && (
            <div className="info-box" style={{ 
              padding: '1rem', 
              background: 'var(--surface-raised)', 
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                {onGetFields 
                  ? '💡 Click "Load Fields from FM" to get available fields via FileMaker script'
                  : '💡 No fields available. Add conditions manually or provide fields list.'}
              </p>
            </div>
          )}

          <div className="conditions-list">
            {conditions.map((condition, index) => (
              <div key={condition.id} className="condition-row">
                {index > 0 && (
                  <select
                    value={condition.logicalOp}
                    onChange={(e) => updateCondition(condition.id, { 
                      logicalOp: e.target.value as 'and' | 'or' 
                    })}
                    className="logical-op-select"
                  >
                    <option value="and">AND</option>
                    <option value="or">OR</option>
                  </select>
                )}

                <input
                  type="text"
                  value={condition.field}
                  onChange={(e) => updateCondition(condition.id, { field: e.target.value })}
                  placeholder="Field name"
                  list={`fields-${condition.id}`}
                  className="field-input"
                />
                {fields.length > 0 && (
                  <datalist id={`fields-${condition.id}`}>
                    {fields.map(field => (
                      <option key={field} value={field} />
                    ))}
                  </datalist>
                )}

                <select
                  value={condition.operator}
                  onChange={(e) => updateCondition(condition.id, { 
                    operator: e.target.value as QueryCondition['operator']
                  })}
                  className="operator-select"
                >
                  {Object.entries(operatorLabels).map(([op, label]) => (
                    <option key={op} value={op}>{label}</option>
                  ))}
                </select>

                <input
                  type="text"
                  value={condition.value}
                  onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                  placeholder="Value"
                  className="value-input"
                />

                <button
                  onClick={() => removeCondition(condition.id)}
                  className="icon-btn"
                  title="Remove condition"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button 
            onClick={addCondition}
            className="btn btn-secondary btn-sm"
            disabled={fields.length === 0 && !conditions.some(c => c.field)}
          >
            + Add Condition
          </button>

          {conditions.length > 0 && (
            <div className="query-preview" style={{ marginTop: '1rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Generated Filter:
              </label>
              <pre style={{ 
                padding: '0.5rem', 
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.75rem',
                margin: '0.25rem 0 0 0'
              }}>
                {conditions.map((cond, index) => {
                  const value = cond.value.includes(' ') || isNaN(Number(cond.value))
                    ? `'${cond.value}'`
                    : cond.value;
                  const condition = `${cond.field} ${cond.operator} ${value}`;
                  return index === 0 ? condition : `${cond.logicalOp} ${condition}`;
                }).join(' ')}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
